import { useMemo, useState } from 'react';

import { Card, CardHeader } from '../../components/ui/Card';
import { SegmentedControl } from '../../components/ui/Button';
import { DataTable } from '../../components/ui/DataTable';
import { AsyncBoundary, EmptyState, SkeletonRows } from '../../components/ui/AsyncBoundary';
import { StatTile } from '../../components/charts/StatTile';
import { ChartCard, rangeToWindow } from '../../components/charts/ChartCard';
import { ColumnChart } from '../../components/charts/ColumnChart';
import { FleetMap } from '../../components/map/FleetMap';
import { tripColumns } from '../../components/domain/columns';
import { useResource } from '../../hooks/useResource';
import * as fmt from '../../lib/format';

/**
 * Trips: the summary first, then the daily shape, then the list.
 *
 * Selecting a trip draws the road it actually took, from the position-valid
 * points recorded between its own timestamps. Low-confidence fixes are already
 * excluded server-side, so a scattered GPS point cannot draw a spike across
 * the city.
 *
 * When a device reported no usable fixes for a whole trip there is nothing to
 * draw, and the panel says so rather than joining start to end with a straight
 * line — that line would be a road the vehicle never took.
 */
export function TripsTab({ vehicleId }) {
  const [range, setRange] = useState('7d');
  const [selected, setSelected] = useState(null);

  const window = useMemo(() => rangeToWindow(range), [range]);
  const trips = useResource(`/api/vehicles/${vehicleId}/trips`, {
    from: window.from,
    to: window.to,
    limit: 200,
  });

  /**
   * The path the vehicle actually drove during the selected trip.
   *
   * Start and end markers alone say where a journey began and finished but
   * nothing about how it got there — which road, which detour, how long it sat
   * at one junction. The track comes from the position-valid points recorded
   * between the trip's own timestamps, so it is the real path rather than a
   * line drawn between two dots.
   */
  const track = useResource(
    selected ? `/api/vehicles/${vehicleId}/track` : null,
    selected
      ? { from: selected.started_at, to: selected.ended_at || new Date().toISOString() }
      : null
  );

  const routeCoordinates = track.data?.geometry?.coordinates || [];

  const rangeControl = (
    <SegmentedControl
      value={range}
      onChange={setRange}
      options={[
        { value: '24h', label: '24H' },
        { value: '7d', label: '7D' },
        { value: '30d', label: '30D' },
      ]}
    />
  );

  return (
    <AsyncBoundary state={trips} skeleton={<SkeletonRows rows={6} />}>
      {(data) => {
        const rows = data.trips || [];

        // A trip still running counts. Ingest writes its running totals every
        // minute, so excluding it would report zero for a vehicle that has
        // been driving all morning.
        const totals = rows.reduce(
          (acc, trip) => ({
            distance: acc.distance + Number(trip.distance_km || 0),
            idle: acc.idle + Number(trip.idle_s || 0),
            max: Math.max(acc.max, Number(trip.max_speed || 0)),
            driving:
              acc.driving +
              ((trip.ended_at ? new Date(trip.ended_at) : new Date()) - new Date(trip.started_at)) /
                1000,
          }),
          { distance: 0, idle: 0, max: 0, driving: 0 }
        );

        const daily = groupByDay(rows);

        return (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {rangeControl}
              <span className="text-[12.5px] text-ink-3">
                {rows.length} trip{rows.length === 1 ? '' : 's'} in this range
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatTile label="Distance" value={Number(totals.distance.toFixed(1))} unit="km" icon="route" tone="brand" compact />
              <StatTile label="Driving time" value={fmt.duration(totals.driving)} icon="clock" tone="brand" compact />
              <StatTile label="Idle time" value={fmt.duration(totals.idle)} icon="pause" tone="warning" compact />
              <StatTile label="Top speed" value={Math.round(totals.max)} unit="km/h" icon="arrow" tone="accent" compact />
            </div>

            {daily.length > 1 && (
              <ChartCard
                title="Distance per day"
                subtitle={`${window.label} · kilometres`}
                icon="chart"
                table={{
                  columns: [
                    { key: 'label', header: 'Day' },
                    { key: 'value', header: 'km', align: 'right' },
                  ],
                  rows: daily,
                }}
              >
                <ColumnChart
                  data={daily}
                  height={190}
                  highlightLast
                  ariaLabel="Distance travelled per day"
                  formatValue={(v) => String(Math.round(v))}
                />
              </ChartCard>
            )}

            <div className="grid gap-4 xl:grid-cols-[1.5fr_1fr]">
              <Card className="overflow-hidden">
                <CardHeader title="Trips" icon="route" />
                {rows.length ? (
                  <DataTable
                    columns={tripColumns()}
                    rows={rows}
                    rowKey={(row) => row.id}
                    onRowClick={setSelected}
                    defaultSort={{ key: 'started_at', direction: 'desc' }}
                  />
                ) : (
                  <EmptyState
                    icon="route"
                    title="No trips recorded"
                    description="A trip opens when the vehicle starts moving and closes after it has been stationary for five minutes."
                  />
                )}
              </Card>

              <Card className="overflow-hidden">
                <CardHeader
                  title={selected ? 'Route travelled' : 'Trip detail'}
                  subtitle={
                    selected
                      ? track.loading
                        ? 'Loading the path…'
                        : routeCoordinates.length > 1
                          ? `${fmt.dateTime(selected.started_at)} · ${fmt.number(routeCoordinates.length)} points`
                          : `${fmt.dateTime(selected.started_at)} · no position-valid points recorded`
                      : 'Choose a trip to see the road it took'
                  }
                  icon="route"
                />
                <div className="p-3">
                  <FleetMap
                    height={330}
                    route={routeCoordinates}
                    vehicles={
                      selected
                        ? [
                            selected.start_lat && {
                              id: `${selected.id}-start`,
                              reg_number: 'Start',
                              last_lat: selected.start_lat,
                              last_lon: selected.start_lon,
                              device_id: 'x',
                              online: true,
                              vehicle_state: 'parked',
                            },
                            selected.end_lat && {
                              id: `${selected.id}-end`,
                              reg_number: 'End',
                              last_lat: selected.end_lat,
                              last_lon: selected.end_lon,
                              device_id: 'x',
                              online: true,
                              vehicle_state: 'moving',
                            },
                          ].filter(Boolean)
                        : []
                    }
                  />
                </div>
                {selected && !track.loading && routeCoordinates.length <= 1 && (
                  <p className="border-t border-line px-4 py-2.5 text-[12.5px] text-ink-3">
                    The device reported no usable GNSS fixes during this trip, so there is no path
                    to draw. Start and end are the last known positions either side of it.
                  </p>
                )}
              </Card>
            </div>
          </div>
        );
      }}
    </AsyncBoundary>
  );
}

function groupByDay(trips) {
  const buckets = new Map();
  for (const trip of trips) {
    const day = new Date(trip.started_at);
    day.setHours(0, 0, 0, 0);
    const key = day.getTime();
    buckets.set(key, (buckets.get(key) || 0) + Number(trip.distance_km || 0));
  }

  return [...buckets.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([time, value]) => ({
      key: time,
      label: new Date(time).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }),
      value: Number(value.toFixed(1)),
      tooltipLabel: 'Distance (km)',
    }));
}
